import { Alert, Severity, theme, DeprecatedErrorWarningIcon, ChevronRightIcon } from '@arnold/common';
import { CREDITS_USAGE_THRESHOLD, ProductCode } from '@arnold/core';
import styled from '@emotion/styled/macro';
import { differenceInDays, startOfDay } from 'date-fns';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { ErrorWarning } from '../../assets/images';
import { OrganizationLicence } from '../../lib/types';

interface ILicenceInfoProps {
  organizationLicence: OrganizationLicence;
  product?: string;
}

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${theme.colors.borderMain.default};
  color: ${theme.colors.text.secondary};
  padding: ${theme.spacing.e} ${theme.spacing.f};
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${theme.colors.text.primary};
    border-top: 1px solid ${theme.colors.text.secondary};

    & svg path {
      fill: ${theme.colors.text.primary};
      transition: fill 0.2s ease-in-out;
    }
  }

  & svg path {
    fill: ${theme.colors.text.secondary};
  }
`;

const WarningFlag = styled.img`
  margin-right: 5.75px;
  width: 13.5px;
  position: relative;
  top: -1.5px;
`;

const LicenceInfo: FunctionComponent<ILicenceInfoProps> = ({ organizationLicence }) => {
  const [t] = useTranslation(['licenceInfo', 'common']);

  if (!organizationLicence) {
    return null;
  }
  const today = startOfDay(new Date());
  const validTo = new Date(organizationLicence.validTo);
  const numberOfDaysToExpiration = differenceInDays(startOfDay(validTo), today);

  const usedCredits = organizationLicence.usedCredits || 0;
  const boughtCredits = organizationLicence.boughtCredits || 0;
  const transferredCredits = organizationLicence.transferredCredits || 0;
  const totalCredits = boughtCredits + transferredCredits;

  const isLimitExceeded = usedCredits > totalCredits;
  const isNearLimit = usedCredits > totalCredits * CREDITS_USAGE_THRESHOLD;

  if (
    organizationLicence?.licenceCount &&
    organizationLicence.product === ProductCode.Business &&
    organizationLicence.activeRespondentsInCurrentPeriodCount &&
    organizationLicence.activeRespondentsInCurrentPeriodCount > organizationLicence.licenceCount
  ) {
    return (
      <Alert hideIcon severity={Severity.DANGER}>
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <WarningFlag src={ErrorWarning} />
            {t('limitExceeded')}
          </div>
          <ChevronRightIcon width={20} height={20} />
        </div>
      </Alert>
    );
  }
  if (organizationLicence.product === ProductCode.Credits && isLimitExceeded) {
    return (
      <Alert hideIcon severity={Severity.DANGER}>
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <WarningFlag src={ErrorWarning} />
            {t('minusCredits', { credits: usedCredits - totalCredits })}
          </div>
          <ChevronRightIcon width={20} height={20} />
        </div>
      </Alert>
    );
  }

  if (organizationLicence.product === ProductCode.Free) {
    return (
      <Alert hideIcon severity={Severity.WARNING}>
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center">{t('upgrade')}</div>
          <ChevronRightIcon width={20} height={20} />
        </div>
      </Alert>
    );
  }

  if (numberOfDaysToExpiration <= 30) {
    return (
      <Alert hideIcon severity={Severity.WARNING}>
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <DeprecatedErrorWarningIcon width={20} height={20} className="mr-3" />
            {t('expires')}
          </div>
          <ChevronRightIcon width={20} height={20} />
        </div>
      </Alert>
    );
  }

  if (organizationLicence.product === ProductCode.Credits && isNearLimit) {
    return (
      <Alert hideIcon severity={Severity.WARNING}>
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <DeprecatedErrorWarningIcon width={20} height={20} className="mr-3" />
            {t('lastCredits', { credits: totalCredits - usedCredits })}
          </div>
          <ChevronRightIcon width={20} height={20} />
        </div>
      </Alert>
    );
  }

  if (organizationLicence.product === ProductCode.Credits) {
    return (
      <InfoContainer>
        <span>{t('lastCredits', { credits: totalCredits - usedCredits })}</span>
        <ChevronRightIcon width={20} height={20} />
      </InfoContainer>
    );
  }
  if (organizationLicence.product === ProductCode.Business) {
    return (
      <InfoContainer>
        <span>{t(`editionSettings:label-${organizationLicence.product}`)}</span>
        <ChevronRightIcon width={20} height={20} />
      </InfoContainer>
    );
  }

  return null;
};

const LicenceInfoWrapper: FunctionComponent<ILicenceInfoProps> = (props) => {
  const history = useHistory();
  return (
    <div
      role="button"
      className="mx-6 mt-auto mb-8 w-100"
      onClick={() => {
        history.push('/settings');
      }}
    >
      <LicenceInfo {...props} />
    </div>
  );
};

export default LicenceInfoWrapper;
